import React, { useState, useEffect, useRef } from 'react';
import { db } from '../Components/auth/firebaseConfig';
import { getCookie } from '../utils/cookieUtils';
import { Link as RouterLink } from 'react-router-dom';

import {
  Container, DialogActions, FormControl, InputLabel, Select, MenuItem, Box, Typography, Button, Dialog, DialogTitle, DialogContent, TextField, Alert, Grid, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ProjectCard from '../Components/ProjectCard';
import Navbar from '../Components/LandingComponent/Navbar';
import { addProject, editProject, deleteProject, fetchProjects, createTemplate, fetchUserDetails, fetchUserIdByEmail, fetchTemplates, updateAllTemplatesWithWithdrawnColumn, fetchLastSerialNumber } from '../DatabaseApis/DBQuery';
import { useNavigate } from 'react-router-dom';
import SideDrawer from '../Components/newcomponents/SideDrawer';
import axios from 'axios';
import AddTemplateDialog from './AddTemplateDialog';
// import {jwtDecode} from 'jwt-decode';
// import MilestoneChart from './MilestoneChart';
import { getFirestore, collection, getDoc, doc, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { getSuperAdminEmails } from '../utils/superAdminUtils';

const projectIdentity = (project = {}) => [
  String(project.tenantId || '').trim().toLowerCase(),
  String(project.prjName || '').trim().toLowerCase(),
].join('::');

const mergeUniqueProjects = (projects = []) => {
  const uniqueById = new Map();
  projects.forEach((project) => {
    if (project?.id) uniqueById.set(project.id, project);
  });

  const uniqueByName = new Map();
  uniqueById.forEach((project) => {
    const key = projectIdentity(project);
    const existing = uniqueByName.get(key);
    if (!existing || String(project.createdAt || '') < String(existing.createdAt || '')) {
      uniqueByName.set(key, project);
    }
  });

  return [...uniqueByName.values()];
};

const ProjectPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    prjName: '',
    startDate: '',
    dueDate: '',
    description: '',
  });
  // const [isTemplateSelected, setIsTemplateSelected] = useState(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [templates, setTemplates] = useState([]);  // State to hold templates
  // const [selectedTemplate, setSelectedTemplate] = useState('ZppFzGexYOwrN9stfIGO'); // State for selected template
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [editProjectData, setEditProjectData] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false); // For delete confirmation
  const [error, setError] = useState(null); // For error messages
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const infiniteScrollRef = useRef(null);
  const projectSaveInFlightRef = useRef(false);
  // Fetch user data from session storage
  const userData = getCookie('userData');
  // const decodedToken = jwtDecode(userData.token);
  // const userRole = decodedToken.role;
  // const tenantId=decodedToken.tenantId;
  // const email = decodedToken.email;
  const [email, setEmail] = useState(userData?.email || '');
  const [tenantId, setTenantId] = useState(userData?.tenantId || '');
  const [userRole, setUserRole] = useState(userData?.role || '');
  const [userId, setUserId] = useState(userData?.userId || ''); // Declare userId as state
  const [userName, setUserName] = useState(userData?.userName || ''); // Declare userName as state
  const [tenantLogoUrl, setTenantLogoUrl] = useState('');
  const [tenantOrganizationName, setTenantOrganizationName] = useState('');

  const superadminEmails = getSuperAdminEmails();

  const isSuperAdmin = superadminEmails.includes(userData.email.toLowerCase());


  useEffect(() => {
    const getUserDetails = async () => {
        console.log(userData);
        const token = userData?.token;

        // If we already have the essentials from session cookie, page can continue loading.
        if (!token) {
          if (!tenantId) {
            console.error('Token not found in cookie');
            setError('Authentication token not found.');
          }
          return;
        }
        
        // Skip if we already have the data
        if (email && tenantId && userRole && userId && userName) {
          console.log('User details already loaded, skipping fetch');
          return;
        }
        
        try {
          const data = await fetchUserDetails(token);
          console.log(data);
          setEmail(data.email);
          setTenantId(data.tenantId);
          setUserRole(data.role);
          
          // Fetch userId and userName after getting email
          const userInfo = await fetchUserIdByEmail(data.email, data.tenantId);
          if (userInfo) {
            setUserId(userInfo.userId);
            setUserName(userInfo.userName);
          }

        } catch (error) {
          console.error('Error fetching user details:', error);
          setError('Failed to fetch user details: ' + error.message);
        }
    };
    getUserDetails();
  }, []); // Empty dependency array - run only once on mount

  useEffect(() => {
    const checkTenantStatus = async () => {
      if (!tenantId) return;
      // Query for the tenant document where tenantId field matches
      const tenantsRef = collection(db, "tenant");
      const q = query(tenantsRef, where("tenantId", "==", tenantId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const tenantDoc = querySnapshot.docs[0];
        const tenantData = tenantDoc.data();
        const status = tenantData.status;
        setTenantLogoUrl(tenantData.companyLogoUrl || '');
        setTenantOrganizationName(tenantData.companyName || tenantData.tenantName || tenantId || 'Organization');
        if (status === 'inactive') {
          setError("Your tenant is inactive. Please contact your administrator.");
          navigate('/'); // or navigate to a dedicated error page
        }
      } else {
        // Tenant not found, handle as needed
        setError("Tenant not found. Please contact your administrator.");
        navigate('/');
      }
    };
    checkTenantStatus();
  }, [tenantId, navigate]);

  const loadProjects = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    const RETRY_LIMIT = 3;
    const RETRY_DELAY = 2000;
    let attempts = 0;
    let success = false;

    while (attempts < RETRY_LIMIT && !success) {
      try {
        const { projects: newProjects, lastVisible: lastDoc } = await fetchProjects(lastVisible, tenantId);

        if (newProjects.length === 0) {
          setHasMore(false);
        }

        setProjects((prevProjects) => {
          return mergeUniqueProjects([...prevProjects, ...newProjects]);
        });

        setLastVisible(lastDoc);
        success = true;
      } catch (error) {
        attempts += 1;
        console.error("Error fetching projects, attempt:", attempts, error);
        if (attempts >= RETRY_LIMIT) {
          console.error("Max retry attempts reached. Could not fetch projects.");
          setError("Could not fetch projects after multiple attempts.");
        } else {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (tenantId && projects.length === 0) {
      loadProjects();
    }
  }, [tenantId, projects.length]); // Use projects.length instead of projects array to prevent unnecessary calls

  useEffect(() => {
    const target = infiniteScrollRef.current;
    if (!target || !tenantId) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading) {
          loadProjects();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.1,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [tenantId, hasMore, loading, lastVisible]);

  const loadTemplates = async () => {
      try {
        const templateList = await fetchTemplates(tenantId); // Use the fetchTemplates function
        setTemplates(templateList);
        
        // Do not block project page rendering on global template migration.
        updateAllTemplatesWithWithdrawnColumn().catch((error) => {
          console.error("Background template update failed:", error);
        });
      } catch (error) {
        console.error("Error loading templates:", error);
      }
    };

  useEffect(() => {
  if (tenantId && templates.length === 0) {
    loadTemplates();
  }
}, [tenantId, templates.length]); // Add templates.length to prevent unnecessary calls

// useEffect(() => {
//   if (!tenantId) return;

//   // Listen for both tenant and global templates
//   const templatesCollection = collection(db, "templates");
//   const tenantQ = query(templatesCollection, where("tenantId", "==", tenantId));
//   const globalQ = query(templatesCollection, where("global", "==", true));

//   // Listen for tenant templates
//   const unsubTenant = onSnapshot(tenantQ, (tenantSnap) => {
//     const tenantTemplates = tenantSnap.docs.map(doc => ({
//       id: doc.id,
//       ...doc.data(),
//     }));

//     // Listen for global templates
//     const unsubGlobal = onSnapshot(globalQ, (globalSnap) => {
//       const globalTemplates = globalSnap.docs.map(doc => ({
//         id: doc.id,
//         ...doc.data(),
//       }));

//       // Merge and deduplicate by id
//       const allTemplates = [...tenantTemplates, ...globalTemplates];
//       const uniqueTemplates = Array.from(
//         new Map(allTemplates.map(t => [t.id, t])).values()
//       );
//       setTemplates(uniqueTemplates);
//     });

//     // Cleanup global listener when tenant changes
//     return () => unsubGlobal();
//   });

//   // Cleanup listeners on unmount or tenantId change
//   return () => unsubTenant();
// }, [tenantId]);

  const handleOpenDialog = () => {
    setEditProjectData(null);
    setNewProject({
      prjName: '',
      startDate: '',
      dueDate: '',
      description: '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  // const handleOpenTemplateDialog = () => setTemplateDialogOpen(true);
  // const handleCloseTemplateDialog = () => setTemplateDialogOpen(false);
  const handleOpenTemplateDialog = () => {
    setOpenTemplateDialog(true);
  };

  // Function to handle closing the Add Template dialog
  const handleCloseTemplateDialog = () => {
    setOpenTemplateDialog(false);
  };

  const handleAddProject = async () => {
    if (projectSaveInFlightRef.current) return;
    projectSaveInFlightRef.current = true;
    setSavingProject(true);
    try {
      const createdProject = await addProject(newProject, tenantId, userId, userName, selectedTemplate);
      setProjects((prevProjects) => mergeUniqueProjects([...prevProjects, createdProject]));
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving project:", error);
      setError("Error saving project: " + error.message);
    } finally {
      projectSaveInFlightRef.current = false;
      setSavingProject(false);
    }
  };  

  const handleEditProject = async () => {
    if (editProjectData && !projectSaveInFlightRef.current) {
      projectSaveInFlightRef.current = true;
      setSavingProject(true);
      try {
        // const tenantId = userData?.tenantId || '';

        const updatedProject = await editProject(editProjectData.id, newProject, tenantId);
        setProjects((prevProjects) =>
          prevProjects.map((p) => (p.id === editProjectData.id ? updatedProject : p))
        );
        handleCloseDialog();
      } catch (error) {
        console.error("Error updating project:", error);
      } finally {
        projectSaveInFlightRef.current = false;
        setSavingProject(false);
      }
    }
  };

  const handleDeleteProject = async () => {
    try {
      await deleteProject(projectToDelete, tenantId);
      setProjects((prevProjects) => prevProjects.filter((project) => project.id !== projectToDelete));
      setShowDeleteAlert(false);
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  };

  const handleLogout = () => {
    console.log('Logout function called');
    sessionStorage.removeItem('userData');
    window.location.href = '/';
  };

  const openEditDialog = (project) => {
    setEditProjectData(project);
    setNewProject({ ...project });
    setDialogOpen(true);
  };

  const confirmDelete = () => {
    handleDeleteProject();
  };

  const handleSaveTemplate = async (templateData) => {
    // const { templateName, columns } = templateData;

    try {
      await createTemplate(
      templateData,
      tenantId,
      userId,
      email,
      isSuperAdmin && templateData.makeGlobal // Pass makeGlobal if superadmin
    );
      console.log('Template saved successfully!');
      setOpenTemplateDialog(false);  // Close dialog after saving
      // await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Error saving template: ' + error.message);
    }
  };

  const projectDialogFieldSx = {
    mt: 1.5,
    '& .MuiInputLabel-root': {
      color: '#5f6b7a',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#1f4fbf',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#f8fbff',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: 'rgba(15, 23, 42, 0.14)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(31, 79, 191, 0.35)',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1f4fbf',
        borderWidth: '1px',
        boxShadow: '0 0 0 3px rgba(31, 79, 191, 0.10)',
      },
    },
    '& .MuiFormHelperText-root': {
      color: '#6b7280',
      marginLeft: 0.5,
    },
  };

  // const handleProjectSelect = (projectId) => {
  //   navigate(/dashboard/${projectId});
  // };
  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId); // Set the selected project
    
    navigate(`/dashboard/${projectId}`);
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    setNewProject({ ...newProject, startDate });

    // Calculate the next day for the "Due Date"
    const nextDay = new Date(startDate);
    nextDay.setDate(nextDay.getDate() + 1); // Add one day

    // Format the next day to 'YYYY-MM-DD'
    const formattedNextDay = nextDay.toISOString().split('T')[0];

    // Set the new minimum for the "Due Date"
    setNewProject((prevProject) => ({
      ...prevProject,
      dueDate: prevProject.dueDate < formattedNextDay ? '' : prevProject.dueDate, // Clear due date if it's before the next day
      minDueDate: formattedNextDay,
    }));
  };
  // const handleTemplateSelection = () => {
  //   setIsTemplateSelected(true); // Call this when a template is selected
  // };
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>} {/* Display error message */}
      <Box>
        <Navbar handleLogout={handleLogout} tenantLogoUrl={tenantLogoUrl} tenantOrganizationName={tenantOrganizationName} />
        <SideDrawer selectedProject={selectedProject} />

        {/* <Container maxWidth="lg" sx={{ mt: 2, p: { xs: 1, sm: 0 } }}> */}

        <Box
          sx={{
            mt: { xs: '64px', sm: '72px' },
            mb: 2.5,
            ml: { xs: '80px', sm: '88px' },
            mr: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: '1px solid #dbe3f3',
            background: 'linear-gradient(135deg, #f7fbff 0%, #eef4ff 55%, #e8f0ff 100%)',
            boxShadow: '0 8px 24px rgba(18, 52, 102, 0.08)',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto' },
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: { xs: 'none', md: 'block' }, width: 64, height: 64, justifySelf: 'start' }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minWidth: 0,
              justifySelf: 'center',
              order: { xs: -1, md: 0 },
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.35rem', sm: '1.8rem' },
                color: '#173a6a',
                lineHeight: 1.15,
                letterSpacing: 0.2,
              }}
            >
              Projects
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              justifySelf: { md: 'end' },
            }}
          >
            {userRole === 'Admin' && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  sx={{
                    px: 2,
                    py: 1,
                    minHeight: 42,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    boxShadow: '0 8px 18px rgba(37, 99, 235, 0.28)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Add Project
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenTemplateDialog}
                  sx={{
                    px: 2,
                    py: 1,
                    minHeight: 42,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#1f4fa8',
                    borderColor: '#b8cdf6',
                    backgroundColor: 'rgba(255,255,255,0.72)',
                    '&:hover': {
                      borderColor: '#7ea6ea',
                      backgroundColor: '#ffffff',
                    },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Add Template
                </Button>
              </>
            )}
          </Box>
        </Box>

        {showDeleteAlert && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" onClick={confirmDelete}>
                Confirm
              </Button>
            }
            onClose={() => setShowDeleteAlert(false)}
            sx={{ mb: 2, marginLeft: '15px' }}
          >
            Are you sure you want to delete this project?
          </Alert>
        )}

        {/* Container for Project Cards */}
        <Box
          sx={{
            flexGrow: 1,
            marginLeft: { xs: '80px', sm: '88px' },
            marginTop: { xs: '20px', md: '0' },
            pr: 2,
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              margin: 0,
            }}
          >
            {/* Project Cards arranged in an NxN way */}
            {projects.map((project, index) => (
              <Grid
                item
                xs={12} // Takes full width on extra small screens
                sm={6}  // 2 columns on small screens
                md={4}  // 3 columns on medium screens
                lg={3}  // 4 columns on large screens
                key={project.id}
              >
                <Box
                  sx={{
                    padding: { xs: '4px', sm: '8px' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'stretch',
                  }}
                  onClick={() => handleProjectSelect(project.id)}
                >
                  <ProjectCard
                    project={project}
                    onEdit={() => {
                      if (userRole === 'Admin') {
                        openEditDialog(project);
                      }
                    }}
                    onDelete={() => {
                      if (userRole === 'Admin') {
                        setProjectToDelete(project.id);
                        setShowDeleteAlert(true);
                      }
                    }}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {loading && (
          <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
            Loading...
          </Typography>
        )}

        <Box ref={infiniteScrollRef} sx={{ height: 24, width: '100%' }} />

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              border: '1px solid rgba(148, 163, 184, 0.22)',
              boxShadow: '0 22px 60px rgba(15, 23, 42, 0.22)',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              textAlign: 'center',
              fontSize: { xs: '1.8rem', sm: '2rem' },
              fontWeight: 800,
              letterSpacing: '-0.02em',
              fontFamily: '"Segoe UI", "Poppins", "Inter", sans-serif',
              color: '#0f172a',
            }}
          >
            {editProjectData ? 'Edit Project' : 'Add Project'}
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Project Name"
              fullWidth
              sx={projectDialogFieldSx}
              value={newProject.prjName}
              onChange={(e) => setNewProject({ ...newProject, prjName: e.target.value.slice(0, 75) })}
              helperText={`${newProject.prjName.length}/75`}
              inputProps={{ maxLength: 75 }}
            />

            <TextField
              margin="dense"
              label="Start Date"
              type="date"
              fullWidth
              sx={projectDialogFieldSx}
              value={newProject.startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0], // Set minimum date to today
              }}
            />


            <TextField
              margin="dense"
              label="Due Date"
              type="date"
              fullWidth
              sx={projectDialogFieldSx}
              value={newProject.dueDate}
              onChange={(e) => setNewProject({ ...newProject, dueDate: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: newProject.minDueDate || new Date().toISOString().split('T')[0], // Set minimum due date to next day
              }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              sx={projectDialogFieldSx}
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value.slice(0, 300) })}
              helperText={`${newProject.description.length}/300`}
              inputProps={{ maxLength: 300 }}
            />
            <FormControl
              fullWidth
              margin="normal"
              sx={{
                mt: 2,
                '& .MuiInputLabel-root': {
                  color: '#5f6b7a',
                  fontWeight: 500,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#1f4fbf',
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fbff',
                  '& fieldset': {
                    borderColor: 'rgba(15, 23, 42, 0.14)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(31, 79, 191, 0.35)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1f4fbf',
                    boxShadow: '0 0 0 3px rgba(31, 79, 191, 0.10)',
                  },
                },
              }}
            >
              <InputLabel>Template</InputLabel>
              <Select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                label="Template"
                sx={{
                  borderRadius: 2,
                  '& .MuiSelect-select': {
                    py: 1.8,
                  },
                }}
                // disabled={isTemplateSelected}
              >
                {(() => {
                  console.log('Templates loaded for dropdown:', templates);
                  return templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.templateName}
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>
          </DialogContent>

         
          <DialogActions sx={{ px: 0, pt: 1, pb: 0 }}>
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              p={2}
              sx={{
                borderTop: '1px solid rgba(148, 163, 184, 0.18)',
                background: 'rgba(255,255,255,0.75)',
              }}
            >
              {/* Footer content (e.g., message or info) */}
              <Box mb={2}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Please review the project details before saving.
                </Typography>
              </Box>

              {/* Buttons aligned to the right */}
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  onClick={editProjectData ? handleEditProject : handleAddProject}
                  variant="contained"
                  color="primary"
                  disabled={savingProject}
                  startIcon={savingProject ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: '0 10px 20px rgba(37, 99, 235, 0.22)',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    },
                  }}
                >
                  {savingProject ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={handleCloseDialog}
                  disabled={savingProject}
                  sx={{
                    px: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#1f4fbf',
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </DialogActions>

        </Dialog>
        <AddTemplateDialog
            //  isTemplateSelected={isTemplateSelected} 
            open={openTemplateDialog}
            onClose={handleCloseTemplateDialog}
            onSave={handleSaveTemplate}
            isSuperAdmin={isSuperAdmin}
          />
        {/* </Container> */}
      </Box>
    </>
  );

};

export default ProjectPage;


